import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Plus, Heart, BookOpen, Target, Compass } from "lucide-react";
import ClientShell from "./ClientShell";
import { useWorkspaceHome } from "../../lib/rehearsalRoomQueries";
import { useFavoriteAssignments } from "../../lib/practiceQueries";
import {
  usePresenceContexts, usePresenceContextDetail, useCreatePresenceContext,
  useCreatePresenceApplication, usePresenceReflections,
} from "../../lib/presenceQueries";

const PROGRESS_LABEL = {
  not_started: "Not started", exploring: "Exploring", practising: "Practising", integrating: "Integrating", applied: "Applied",
};
const PROGRESS_COLOR = {
  not_started: "bg-ink/10 text-ink-soft", exploring: "bg-mustard/20 text-mustard-dark",
  practising: "bg-terracotta/15 text-terracotta", integrating: "bg-ink-blue/15 text-ink-blue", applied: "bg-emerald-100 text-emerald-700",
};

/**
 * Master v5 Prompt 21 — Presence Lab. Organizes contexts, experiments
 * (the member's own Practice content, linked via each context's
 * layer_key), applications, reflections and personal toolkit — a
 * client-facing view of ONLY their own real leadership situations,
 * never the raw Coach Library, never a numeric score, never a fixed
 * curriculum (contexts are freely created, in any order).
 */
export default function PresenceLabPage() {
  const { workspaceId, contextId } = useParams();
  const navigate = useNavigate();
  const home = useWorkspaceHome(workspaceId);
  const memberId = home.data?.viewer_membership_id;
  const [tab, setTab] = useState("contexts");

  if (!home.data) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-paper" data-testid="presence-lab-loading">
        <p className="text-ink-soft">Loading…</p>
      </section>
    );
  }

  return (
    <ClientShell>
      <div className="max-w-2xl" data-testid="presence-lab-page">
        <button
          onClick={() => (contextId ? navigate(`/rehearsal-room/w/${workspaceId}/presence`) : navigate(`/rehearsal-room/w/${workspaceId}/home`))}
          className="flex items-center gap-1.5 text-sm text-ink-soft hover:text-terracotta transition-colors mb-6"
          data-testid="presence-lab-back-link"
        >
          <ArrowLeft size={14} /> {contextId ? "Back to Presence Lab" : "Back to Home"}
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} className="text-terracotta" />
          <h1 className="font-serif text-2xl text-ink">Presence Lab</h1>
        </div>
        <p className="text-ink-soft mb-6">Real leadership situations, your coach's focus, and how you're applying it.</p>

        {contextId ? (
          <ContextDetail workspaceId={workspaceId} memberId={memberId} contextId={contextId} journeyRail={home.data.journey_rail} navigate={navigate} />
        ) : (
          <>
            <div className="flex items-center gap-4 mb-5 border-b border-ink/10">
              {[["contexts", "Contexts"], ["reflections", "Reflections"], ["toolkit", "Personal Toolkit"]].map(([key, label]) => (
                <button key={key} onClick={() => setTab(key)} className={`text-sm pb-2 px-1 ${tab === key ? "text-terracotta border-b-2 border-terracotta" : "text-ink-soft"}`} data-testid={`presence-lab-tab-${key}`}>
                  {label}
                </button>
              ))}
            </div>
            {tab === "contexts" && <ContextsList workspaceId={workspaceId} memberId={memberId} navigate={navigate} />}
            {tab === "reflections" && <ReflectionsList workspaceId={workspaceId} />}
            {tab === "toolkit" && <ToolkitList workspaceId={workspaceId} />}
          </>
        )}
      </div>
    </ClientShell>
  );
}

function ContextsList({ workspaceId, memberId, navigate }) {
  const { data, isPending } = usePresenceContexts(workspaceId, memberId);
  const [creating, setCreating] = useState(false);
  const items = data?.items || [];

  return (
    <div data-testid="presence-contexts-list">
      <button
        onClick={() => setCreating((v) => !v)}
        className="flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80 mb-4"
        data-testid="presence-context-new-button"
      >
        <Plus size={14} /> New leadership context
      </button>
      {creating && <NewContextForm workspaceId={workspaceId} memberId={memberId} onDone={() => setCreating(false)} />}

      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="presence-contexts-loading">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="presence-contexts-empty">No leadership contexts yet — add the first real situation you're navigating.</p>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <button
              key={c.psc_id}
              onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/presence/${c.psc_id}`)}
              className="w-full text-left border border-ink/15 bg-white/50 rounded-lg p-4 hover:border-terracotta/40 transition-colors"
              data-testid={`presence-context-card-${c.psc_id}`}
            >
              <p className="font-serif text-base text-ink">{c.title}</p>
              {c.situation_text && <p className="text-sm text-ink-soft mt-1 line-clamp-2">{c.situation_text}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NewContextForm({ workspaceId, memberId, onDone }) {
  const [form, setForm] = useState({ title: "", situation_text: "", stakeholders_text: "", desired_impact_text: "", context_notes: "" });
  const create = useCreatePresenceContext(workspaceId, memberId);

  async function handleSubmit() {
    if (!form.title.trim()) return;
    await create.mutateAsync(form);
    onDone();
  }

  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-4 mb-4 space-y-2" data-testid="presence-context-new-form">
      <input placeholder="Title (e.g. Board Q&A on layoffs)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="presence-context-title-input" />
      <textarea placeholder="What's the situation?" value={form.situation_text} onChange={(e) => setForm({ ...form, situation_text: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" rows={2} data-testid="presence-context-situation-input" />
      <input placeholder="Who's involved (stakeholders)?" value={form.stakeholders_text} onChange={(e) => setForm({ ...form, stakeholders_text: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="presence-context-stakeholders-input" />
      <input placeholder="What impact do you want to have?" value={form.desired_impact_text} onChange={(e) => setForm({ ...form, desired_impact_text: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="presence-context-impact-input" />
      <input placeholder="When / any other context" value={form.context_notes} onChange={(e) => setForm({ ...form, context_notes: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="presence-context-notes-input" />
      {create.isError && <p className="text-sm text-red-600" data-testid="presence-context-new-error">Couldn't save this context.</p>}
      <button onClick={handleSubmit} disabled={!form.title.trim() || create.isPending} className="text-sm font-medium bg-terracotta text-paper px-4 py-2 rounded-full hover:bg-terracotta/90 disabled:opacity-50" data-testid="presence-context-new-submit">
        {create.isPending ? "Saving…" : "Save context"}
      </button>
    </div>
  );
}

function ContextDetail({ workspaceId, memberId, contextId, journeyRail, navigate }) {
  const { data, isPending } = usePresenceContextDetail(workspaceId, memberId, contextId);
  if (isPending || !data) return <p className="text-sm text-ink-soft" data-testid="presence-context-detail-loading">Loading…</p>;

  const { context, foci, applications } = data;
  const currentFocus = foci.find((f) => f.status === "active");
  const railNode = journeyRail?.find((n) => n.stage_key === context.layer_key);

  return (
    <div data-testid={`presence-context-detail-${contextId}`}>
      <h2 className="font-serif text-xl text-ink mb-1">{context.title}</h2>
      <div className="text-sm text-ink-soft space-y-0.5 mb-6">
        {context.situation_text && <p><span className="font-medium text-ink">Situation:</span> {context.situation_text}</p>}
        {context.stakeholders_text && <p><span className="font-medium text-ink">Stakeholders:</span> {context.stakeholders_text}</p>}
        {context.desired_impact_text && <p><span className="font-medium text-ink">Desired impact:</span> {context.desired_impact_text}</p>}
        {context.context_notes && <p><span className="font-medium text-ink">Notes:</span> {context.context_notes}</p>}
      </div>

      {railNode && (
        <button
          onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/practice/${railNode.stage_key}`)}
          className="flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80 mb-6"
          data-testid="presence-context-experiments-link"
        >
          <Compass size={14} /> Go practice this in {railNode.label}
        </button>
      )}

      <section className="mb-6">
        <h3 className="text-sm font-medium text-ink flex items-center gap-1.5 mb-2"><Target size={14} className="text-terracotta" /> Focus</h3>
        {foci.length === 0 ? (
          <p className="text-sm text-ink-soft" data-testid="presence-focus-empty">Your coach hasn't set a focus for this yet.</p>
        ) : (
          <div className="space-y-2">
            {foci.map((f) => (
              <div key={f.psf_id} className={`border rounded-lg p-4 ${f.status === "active" ? "border-terracotta/30" : "border-ink/10 opacity-60"}`} data-testid={`presence-focus-card-${f.psf_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="font-serif text-base text-ink">{f.focus_text}</p>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full shrink-0 ${PROGRESS_COLOR[f.progress_state]}`}>{PROGRESS_LABEL[f.progress_state]}</span>
                </div>
                {f.evidence_text && <p className="text-xs text-ink-soft"><span className="font-medium">Evidence:</span> {f.evidence_text}</p>}
                {f.coach_rationale_text && <p className="text-xs text-ink-soft"><span className="font-medium">Why this focus:</span> {f.coach_rationale_text}</p>}
                {f.application_prompt_text && <p className="text-xs text-ink-blue mt-1">{f.application_prompt_text}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-medium text-ink flex items-center gap-1.5 mb-2"><BookOpen size={14} className="text-terracotta" /> Real-world applications</h3>
        <ApplicationForm workspaceId={workspaceId} memberId={memberId} contextId={contextId} currentFocusId={currentFocus?.psf_id} />
        {applications.length === 0 ? (
          <p className="text-sm text-ink-soft" data-testid="presence-applications-empty">Nothing logged yet — try it for real, then log how it went.</p>
        ) : (
          <div className="space-y-2 mt-3">
            {applications.map((a) => (
              <div key={a.psa_id} className="border border-ink/15 bg-white/50 rounded-lg p-4" data-testid={`presence-application-card-${a.psa_id}`}>
                <p className="text-sm text-ink">{a.description_text}</p>
                {a.outcome_text && <p className="text-sm text-ink-soft mt-1"><span className="font-medium">What happened:</span> {a.outcome_text}</p>}
                <p className="text-xs text-ink-soft mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ApplicationForm({ workspaceId, memberId, contextId, currentFocusId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ description_text: "", outcome_text: "" });
  const create = useCreatePresenceApplication(workspaceId, memberId, contextId);

  async function handleSubmit() {
    if (!form.description_text.trim()) return;
    await create.mutateAsync({ ...form, psf_id: currentFocusId || null });
    setForm({ description_text: "", outcome_text: "" });
    setOpen(false);
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80" data-testid="presence-application-new-button">
        <Plus size={14} /> Log an application
      </button>
    );
  }
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-4 space-y-2" data-testid="presence-application-new-form">
      <textarea placeholder="What did you try, and where?" value={form.description_text} onChange={(e) => setForm({ ...form, description_text: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" rows={2} data-testid="presence-application-description-input" />
      <textarea placeholder="What happened?" value={form.outcome_text} onChange={(e) => setForm({ ...form, outcome_text: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" rows={2} data-testid="presence-application-outcome-input" />
      {create.isError && <p className="text-sm text-red-600" data-testid="presence-application-new-error">Couldn't save this.</p>}
      <button onClick={handleSubmit} disabled={!form.description_text.trim() || create.isPending} className="text-sm font-medium bg-terracotta text-paper px-4 py-2 rounded-full hover:bg-terracotta/90 disabled:opacity-50" data-testid="presence-application-new-submit">
        {create.isPending ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

function ReflectionsList({ workspaceId }) {
  const { data, isPending } = usePresenceReflections(workspaceId);
  const items = data?.items || [];
  if (isPending) return <p className="text-sm text-ink-soft" data-testid="presence-reflections-loading">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-ink-soft" data-testid="presence-reflections-empty">No reflections yet — these appear after you complete a practice exercise.</p>;
  return (
    <div className="space-y-2" data-testid="presence-reflections-list">
      {items.map((r) => (
        <div key={r.rfl_id} className="border border-ink/15 bg-white/50 rounded-lg p-4" data-testid={`presence-reflection-card-${r.rfl_id}`}>
          <p className="text-sm text-ink">{r.response_text}</p>
        </div>
      ))}
    </div>
  );
}

function ToolkitList({ workspaceId }) {
  const { data, isPending } = useFavoriteAssignments(workspaceId);
  const items = data?.items || [];
  if (isPending) return <p className="text-sm text-ink-soft" data-testid="presence-toolkit-loading">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-ink-soft" data-testid="presence-toolkit-empty">Nothing saved yet — tap the heart on anything assigned to you to keep it here.</p>;
  return (
    <div className="space-y-2" data-testid="presence-toolkit-list">
      {items.map((row) => (
        <div key={row.assignment.asg_id} className="border border-ink/15 bg-white/50 rounded-lg p-4 flex items-center gap-2" data-testid={`presence-toolkit-card-${row.assignment.asg_id}`}>
          <Heart size={14} className="text-terracotta shrink-0" fill="currentColor" />
          <p className="font-serif text-sm text-ink truncate">{row.assignment.title_snapshot}</p>
        </div>
      ))}
    </div>
  );
}
